import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Typography,
  Grid,
  Card,
  Tooltip,
  Box,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
  Chip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import SearchIcon from '@mui/icons-material/Search';
import CustomCard from '../../components/card/CustomCard';
import AppWidgetSummary from '../../sections/@dashboard/app/AppWidgetSummary';
import {
  addProjectComment,
  deleteProjectComment,
  getProjectDashboardSummary,
  getProjectSummaryDetails,
  updateProjectComment,
} from '../../services/projectService';
import {
  calculateProjectHealth,
  getCurrentSprint,
  getLatestTaskInCurrentSprint,
  truncateText,
} from '../../utils/projectHealth';
import formatDate from '../../utils/formatDate';
import AddComment from './details/components/AddComment';
import DeleteModal from '../teams/components/DeleteModal';
import { isAdmin } from '../../utils/share';

export default function ProjectSummaryDashboard() {
  const accountData = useSelector((state) => state?.login?.user);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const initialLoadRef = useRef(true);
  const [projectFilter, setProjectFilter] = useState(null);

  // MAIN COMMENT MODAL (Add new comment)
  const [commentModal, setCommentModal] = useState({
    open: false,
    projectId: null,
  });

  // MENU / EDIT / DELETE STATES
  const [selectedComment, setSelectedComment] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [latestTasks, setLatestTasks] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const loaderRef = useRef(null);

  const navigate = useNavigate();

  const openCommentModal = (projectId) => {
    setCommentModal({ open: true, projectId });
  };

  const closeCommentModal = () => {
    setCommentModal({ open: false, projectId: null });
  };
  const fetchProjects = async ({ reset = false } = {}) => {
    if (loadingMore) return;

    if (isSearching && !reset) return;

    if (!hasMore && !reset) return;

    setLoadingMore(true);

    const currentPage = reset ? 1 : page;

    const res = await getProjectSummaryDetails({
      search,
      page: currentPage,
      limit: 10,
      filter: projectFilter,
    });

    const newProjects = res.data.data.data;

    setProjects((prev) => {
      if (reset) return newProjects;

      const existingIds = new Set(prev.map((p) => p.id));
      const uniqueNew = newProjects.filter((p) => !existingIds.has(p.id));

      return [...prev, ...uniqueNew];
    });

    setHasMore(res.data.data.hasMore);
    setPage(currentPage + 1);
    setLoadingMore(false);

    if (reset) {
      setIsSearching(false);
      initialLoadRef.current = false;
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 2000) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setProjects([]);
    setPage(1);
    setHasMore(true);
    fetchProjects({ reset: true });
  }, [projectFilter]);

  const submitComment = async (text) => {
    setCommentLoading(true);

    const res = await addProjectComment({ comment: text }, commentModal.projectId);

    // backend response
    const backendComment = res.data.data;

    const newComment = {
      ...backendComment,
      projectId: commentModal.projectId, // <--- Required for delete
      commentAuthor: {
        id: accountData.id,
        userName: accountData.userName,
      },
    };

    closeCommentModal();
    setCommentLoading(false);

    setProjects((prev) =>
      prev.map((p) => (p.id === commentModal.projectId ? { ...p, comments: [newComment, ...p.comments] } : p))
    );
  };

  const updateComment = async (text) => {
    if (!selectedComment) return;

    const res = await updateProjectComment(selectedComment.id, { comment: text });

    const updated = {
      ...res.data.data,
      projectId: selectedComment.projectId,
      commentAuthor: selectedComment.commentAuthor,
    };

    setEditModalOpen(false);

    setProjects((prev) =>
      prev.map((p) =>
        p.id === updated.projectId
          ? {
              ...p,
              comments: p.comments.map((c) => (c.id === updated.id ? updated : c)),
            }
          : p
      )
    );
  };

  const removeComment = async () => {
    if (!selectedComment) return;

    await deleteProjectComment(selectedComment.id);

    const pid = selectedComment.projectId;

    setProjects((prev) =>
      prev.map((p) => (p.id === pid ? { ...p, comments: p.comments.filter((c) => c.id !== selectedComment.id) } : p))
    );

    setDeleteModalOpen(false);
  };
  const debouncedSearch = useCallback(
    debounce((value) => {
      setIsSearching(true);
      setProjects([]);
      setPage(1);
      setHasMore(true);
      setSearch(value.trim());
    }, 1000),
    []
  );
  useEffect(() => {
    fetchProjects({ reset: true });
  }, [search]);

  useEffect(() => {
    if (!loaderRef.current) return () => {};

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loadingMore && !isSearching && !initialLoadRef.current) {
          fetchProjects();
        }
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0,
      }
    );

    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [loadingMore, isSearching]);

  useEffect(() => {
    const fetchProjectSummary = async () => {
      setLoading(true);
      const res = await getProjectDashboardSummary();
      setData(res.data.data);
      setLoading(false);
    };
    fetchProjectSummary();
  }, []);
  useEffect(() => {
    if (!projects.length) return;

    const newProjects = projects.slice(-10); // only last page

    const fetchLatestTasks = async () => {
      const entries = await Promise.all(
        newProjects.map(async (p) => {
          const latestTask = await getLatestTaskInCurrentSprint(p?.sprints);
          return [p.id, latestTask];
        })
      );

      setLatestTasks((prev) => ({
        ...prev,
        ...Object.fromEntries(entries),
      }));
    };

    fetchLatestTasks();
  }, [projects]);

  return (
    <>
      <Helmet>
        <title>Project Dashboard</title>
      </Helmet>

      <Typography variant="h6" gutterBottom ml={2}>
        Project Dashboard
      </Typography>

      {/* ====================== TOP CARDS ====================== */}
      <Grid container spacing={4} pl={2} pr={2}>
        <Grid item container xs={12} spacing={4}>
          <Grid container item spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <CustomCard
                value={data?.totalProjects ?? 'N/A'}
                label="Total Active Projects"
                bgColor="linear-gradient(to right, #FFA726 0%, rgba(255,167,38,0) 100%)"
                iconURL="/assets/icons/project-43.svg"
                loading={loading}
                onClick={() => setProjectFilter('')}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <CustomCard
                value={data?.onTrackProjects ?? 'N/A'}
                label="On Track Projects"
                bgColor="linear-gradient(135deg, #22C55E 0%, #065E49 100%)"
                iconURL="/assets/icons/track-on-track.svg"
                loading={loading}
                onClick={() => setProjectFilter('On Track')}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <CustomCard
                value={data?.delayedProjects ?? 'N/A'}
                label="Delayed Project"
                bgColor="linear-gradient(135deg, #8E33FF 0%, #5119B7 100%)"
                iconURL="/assets/icons/delayed-3.svg"
                loading={loading}
                onClick={() => setProjectFilter('Delayed')}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <CustomCard
                value={data?.inDevelopmentProjects ?? 'N/A'}
                label="In Development"
                bgColor="linear-gradient(135deg, #2289c5 0%, #06355e 100%)"
                iconURL="/assets/icons/in-development.svg"
                loading={loading}
                onClick={() => setProjectFilter('In Development')}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <CustomCard
                value={data?.uatProjects ?? 'N/A'}
                label="In UAT"
                bgColor="linear-gradient(135deg, #f13030 0%, #bb1313 100%)"
                iconURL="/assets/icons/quality-testing.svg"
                loading={loading}
                onClick={() => setProjectFilter('UAT')}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <CustomCard
                value={data?.liveProjects ?? 'N/A'}
                label="Live"
                bgColor="linear-gradient(135deg, #803D3B 0%, #803D3B 100%)"
                iconURL="/assets/icons/live-broadcast-4.svg"
                loading={loading}
                onClick={() => setProjectFilter('Live')}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <CustomCard
                value={data?.overBudgetProjects ?? 'N/A'}
                label="Over Budget"
                bgColor="linear-gradient(135deg, #203D3B 0%, #153D3B 100%)"
                iconURL="/assets/icons/over-budget.svg"
                loading={loading}
                onClick={() => setProjectFilter('Over Budget')}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* ====================== ALL PROJECTS ====================== */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ mt: 4, px: 2 }}>
            Projects
          </Typography>
          {projectFilter && (
            <Box sx={{ mt: 4, px: 2 }}>
              <Chip
                label={`Filter: ${projectFilter}`}
                onDelete={() => setProjectFilter(null)}
                color="primary"
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  backgroundColor: '#0288d115',
                }}
              />
            </Box>
          )}
        </Box>
        {
          <TextField
            id="outlined-basic"
            label="Search Project"
            variant="outlined"
            size="small"
            onChange={(e) => debouncedSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        }
      </Box>

      {projectsLoading ? (
        <CircularProgress style={{ display: 'block', margin: '20px auto' }} />
      ) : (
        projects?.map((p, index) => {
          const latest = latestTasks[p.id];
          const latestText = latest?.taskName || 'No Task';

          const isEven = index % 2 === 0;
          return (
            <Card
              key={p.id}
              sx={{
                p: 3,
                m: 2,
                backgroundColor: isEven ? '#f9fafb' : '#fcfcfd',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ mb: 2, fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => navigate(`/project/overview/${p.id}`)}
                >
                  {p.projectName}
                </Typography>
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    background: '#22c55e20',
                    borderRadius: 1,
                    border: '1px solid #0288d1',
                    cursor: 'pointer',
                    width: 'fit-content',
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#0288d1',
                    mb: 2,
                  }}
                  onClick={() =>
                    navigate('/report', {
                      state: {
                        fromProjectDashboard: true,
                        project: {
                          id: p.id,
                          name: p.projectName,
                          clientId: p.clientId,
                          status: p.status,
                        },
                      },
                    })
                  }
                >
                  View Timesheet
                </Box>
              </Box>
              {/* ========= WIDGET SECTION ========= */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3} lg={2.4}>
                  <AppWidgetSummary
                    title="Current Status"
                    total={calculateProjectHealth(p, p?.sprints).health}
                    color="success"
                    sx={{
                      '& .MuiTypography-h4': {
                        fontSize: '1.25rem',
                        fontWeight: 600,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3} lg={2.4}>
                  <AppWidgetSummary
                    title="Risk Status"
                    total={calculateProjectHealth(p, p?.sprints).risk}
                    color="warning"
                    sx={{
                      '& .MuiTypography-h4': {
                        fontSize: '1.25rem',
                        fontWeight: 600,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3} lg={2.4}>
                  <AppWidgetSummary
                    title="Current Sprint"
                    total={getCurrentSprint(p?.sprints)?.name ?? 'No Current Sprint'}
                    color="info"
                    sx={{
                      '& .MuiTypography-h4': {
                        fontSize: '1.25rem',
                        fontWeight: 600,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3} lg={2.4}>
                  <AppWidgetSummary
                    title="Planned UAT Date"
                    total={formatDate(p.projectUATDate)}
                    color="primary"
                    sx={{
                      '& .MuiTypography-h4': {
                        fontSize: '1.25rem',
                        fontWeight: 600,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3} lg={2.4}>
                  <AppWidgetSummary
                    title="Planned Release Date"
                    total={formatDate(p.ProjectLiveDate)}
                    color="primary"
                    sx={{
                      '& .MuiTypography-h4': {
                        fontSize: '1.25rem',
                        fontWeight: 600,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    {/* Latest Task */}
                    <Grid item xs={12} md={6}>
                      <Card
                        sx={{
                          p: 2,
                          alignSelf: 'flex-start',
                          boxShadow: 2,
                          // border: '1px solid #e5e7eb',
                          backgroundColor: isEven ? '#f9fafb' : '#fcfcfd',
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: '#6b7280',
                              mb: 0.5,
                              textTransform: 'uppercase',
                            }}
                          >
                            Latest Task
                          </Typography>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 1.5,
                              py: 0.6,
                              borderRadius: 1,
                              border: '1px solid #0288d1',
                              color: '#0288d1',
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer',
                              width: 'fit-content',
                            }}
                            onClick={() => navigate(`/project/details/${p.id}`)}
                          >
                            View all tasks →
                          </Box>
                        </Box>

                        <Typography sx={{ fontSize: 14, color: '#374151', whiteSpace: 'pre-wrap' }}>
                          {latestText}
                        </Typography>
                      </Card>
                    </Grid>

                    {/* Comments */}
                    <Grid item xs={12} md={6}>
                      <Card
                        sx={{
                          p: 2,
                          height: 'fit-conent',
                          boxShadow: 2,
                          backgroundColor: isEven ? '#f9fafb' : '#fcfcfd',
                          maxHeight: '300px',
                          overflow: 'auto',
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: '#6b7280',
                              mb: 0.5,
                              textTransform: 'uppercase',
                            }}
                          >
                            {(() => {
                              const count = p.comments?.length ?? 0;
                              return `Comments (${count})`;
                            })()}
                          </Typography>

                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.6,
                              borderRadius: 1,
                              border: '1px solid #0288d1',
                              color: '#0288d1',
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                            onClick={() => openCommentModal(p.id)}
                          >
                            + Add Comment
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            maxHeight: 180,
                            overflowY: 'auto',
                          }}
                        >
                          {!p.comments.length ? (
                            <Typography sx={{ fontSize: 14, color: '#374151', whiteSpace: 'pre-wrap' }}>
                              No Comments
                            </Typography>
                          ) : (
                            (p.comments ?? []).slice(0, p.showComments ? undefined : 1).map((c) => (
                              <Box
                                key={c.id}
                                sx={{
                                  mb: 1,
                                  pb: 1,
                                  borderBottom: '1px solid #e5e7eb',
                                  position: 'relative',
                                }}
                              >
                                {accountData.id === c?.commentAuthor?.id && (
                                  <IconButton
                                    size="small"
                                    sx={{ position: 'absolute', top: 0, right: 0 }}
                                    onClick={(e) => {
                                      setSelectedComment(c);
                                      setMenuAnchor(e.currentTarget);
                                    }}
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                )}

                                <Typography sx={{ fontSize: 14, color: '#374151', whiteSpace: 'pre-wrap' }}>
                                  {c.comment}
                                </Typography>

                                <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.5 }}>
                                  {c?.commentAuthor?.userName} • {formatDate(c?.createdAt)}
                                </Typography>
                              </Box>
                            ))
                          )}
                        </Box>

                        {(p.comments?.length ?? 0) > 1 && (
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: '#0288d1',
                              cursor: 'pointer',
                              mt: 0.5,
                            }}
                            onClick={() =>
                              setProjects((prev) =>
                                prev.map((proj) =>
                                  proj.id === p.id ? { ...proj, showComments: !p.showComments } : proj
                                )
                              )
                            }
                          >
                            {p.showComments ? 'Show less' : 'Show more'}
                          </Typography>
                        )}
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Card>
          );
        })
      )}

      {hasMore && (
        <Box
          ref={loaderRef}
          sx={{
            height: 80,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            my: 4,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            setEditModalOpen(true);
            setMenuAnchor(null);
          }}
          disabled={!selectedComment}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteModalOpen(true);
            setMenuAnchor(null);
          }}
          disabled={!selectedComment}
        >
          Delete
        </MenuItem>
      </Menu>

      <AddComment open={commentModal.open} onClose={closeCommentModal} onSubmit={submitComment} />

      <AddComment
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        initialValue={selectedComment?.comment}
        onSubmit={updateComment}
      />

      <DeleteModal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} onDelete={removeComment} />
      {showScrollTop && (
        <Box
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          sx={{
            position: 'fixed',
            bottom: 30,
            right: 30,
            width: 45,
            height: 45,
            borderRadius: '50%',
            backgroundColor: '#0288d1',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: 3,
            zIndex: 1500,
            '&:hover': {
              backgroundColor: '#0277bd',
            },
          }}
        >
          ↑
        </Box>
      )}
    </>
  );
}
